import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, Loader2, ArrowLeft, LogIn } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import api from "@/lib/api";

/**
 * Admin-only gate for the NDIS program pages.
 * The program is in private pilot — the public sees a holding notice, staff see the page.
 */
export default function NdisGate({ children }) {
  const [state, setState] = useState("checking"); // checking | allowed | blocked

  useEffect(() => {
    if (!localStorage.getItem("nc_token")) {
      setState("blocked");
      return;
    }
    api.get("/auth/me")
      .then((r) => setState(r.data?.role === "admin" ? "allowed" : "blocked"))
      .catch(() => setState("blocked"));
  }, []);

  if (state === "checking") {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-nc-blue animate-spin" />
      </div>
    );
  }

  if (state === "blocked") {
    return (
      <div data-testid="ndis-gate-blocked" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
        <SiteNav />
        <main className="flex-1 flex items-center justify-center px-6 py-24">
          <div className="max-w-lg text-center">
            <ShieldAlert className="w-10 h-10 text-nc-orange mx-auto mb-6" />
            <p className="font-mono text-[10px] tracking-[0.35em] text-nc-orange uppercase mb-4">Private Pilot — Under Wraps</p>
            <h1 className="font-sans font-bold text-3xl sm:text-4xl tracking-tight">This program isn't publicly available yet.</h1>
            <p className="text-base text-zinc-400 leading-relaxed mt-5">
              The Specpixel NDIS Creator Program is in a private pilot and not open to the public right now. Details, pricing, and intake are still being finalised — nothing here is live.
            </p>
            <p className="font-mono text-xs text-zinc-500 leading-relaxed mt-4">
              If you're a staff member or pilot partner, sign in with your admin account to continue.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
              <Link data-testid="ndis-gate-home" to="/" className="border border-white/20 hover:border-white/50 text-zinc-200 font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-4 flex items-center justify-center gap-2 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back to Home
              </Link>
              <Link data-testid="ndis-gate-login" to="/admin/login" className="bg-nc-blue hover:bg-nc-blue/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-4 flex items-center justify-center gap-2 transition-colors">
                <LogIn className="w-4 h-4" /> Staff Sign In
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return children;
}
